/**
 * Форма сообщения о проблеме.
 *
 * Одно окно на все случаи: общий баг из шапки, претензия к кадру из разбора
 * раунда, претензия к квесту с его страницы. Разница только в том, что
 * подставлено заранее — тип, уточнение и контекст. Так у модератора все
 * сообщения приходят в одном виде, а игроку не нужно объяснять словами, какой
 * именно кадр был кривым.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSubmitFeedback } from "../api/queries";
import type { FeedbackKind } from "../api/types";
import type { PhotoTopic } from "../lib/feedback";
import { Dialog } from "./Dialog";

interface Props {
  onClose: () => void;
  /** Заголовок окна: у жалобы на кадр он свой. */
  title?: string;
  kind?: FeedbackKind;
  /** Показывать ли выбор типа. У жалоб на кадр и квест он предопределён. */
  lockKind?: boolean;
  /** Показывать ли список уточнений — только для жалоб на кадр. */
  topics?: readonly PhotoTopic[];
  photoId?: string;
  questId?: string;
}

const KINDS: FeedbackKind[] = [
  "FEEDBACK_KIND_BUG",
  "FEEDBACK_KIND_IDEA",
  "FEEDBACK_KIND_OTHER",
];

export function FeedbackDialog({
  onClose,
  title,
  kind: initialKind = "FEEDBACK_KIND_BUG",
  lockKind = false,
  topics,
  photoId,
  questId,
}: Props) {
  const { t } = useTranslation();
  const submit = useSubmitFeedback();

  const [kind, setKind] = useState<FeedbackKind>(initialKind);
  const [topic, setTopic] = useState<PhotoTopic | "">(topics ? topics[0] : "");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <Dialog title={t("feedback.sentTitle")} onClose={onClose}>
        <p className="text-sm text-ink-300">{t("feedback.sentText")}</p>
        <button type="button" className="btn-primary mt-5 w-full" onClick={onClose}>
          {t("common.close")}
        </button>
      </Dialog>
    );
  }

  return (
    <Dialog title={title ?? t("feedback.title")} onClose={onClose}>
      <div className="space-y-4">
        {!lockKind && (
          <div>
            <span className="label">{t("feedback.kindLabel")}</span>
            <div className="flex flex-wrap gap-1.5">
              {KINDS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`chip transition-colors ${
                    kind === value
                      ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                      : "hover:border-ink-600"
                  }`}
                  onClick={() => setKind(value)}
                >
                  {t(`feedback.kind.${value}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {topics && (
          <div>
            <span className="label">{t("feedback.topicLabel")}</span>
            <div className="flex flex-wrap gap-1.5">
              {topics.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`chip transition-colors ${
                    topic === value
                      ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                      : "hover:border-ink-600"
                  }`}
                  onClick={() => setTopic(value)}
                >
                  {t(`feedback.topic.${value}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="label" htmlFor="feedback-message">
            {t("feedback.messageLabel")}
          </label>
          <textarea
            id="feedback-message"
            className="field min-h-[7rem] resize-y"
            placeholder={t("feedback.messagePlaceholder")}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>

        {submit.isError && (
          <p className="text-sm text-rust">
            {submit.error instanceof Error ? submit.error.message : t("feedback.failed")}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={submit.isPending || message.trim().length < 5}
            onClick={() =>
              submit.mutate(
                {
                  kind,
                  ...(topic ? { topic } : {}),
                  message: message.trim(),
                  ...(photoId ? { photoId } : {}),
                  ...(questId ? { questId } : {}),
                },
                { onSuccess: () => setSent(true) },
              )
            }
          >
            {submit.isPending ? t("feedback.sending") : t("feedback.send")}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
